import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';

const featured = [
  { slug: 'tolvkanten', title: 'Tolvkanten', w: 1200, h: 1500 },
  { slug: 'zoolab', title: 'ZooLab', w: 1200, h: 1130 },
  { slug: 'store-taarn', title: 'Store Tårn', w: 1200, h: 800 },
  { slug: 'rosenborg', title: 'Rosenborg', w: 1200, h: 1800 },
  { slug: 'zoologisk-have', title: 'Zoologisk Have', w: 1200, h: 675 },
];

const img = (slug, size) => `/images/udvalgte/${slug}-${size}.webp`;

const ArrowButton = ({ dir, onClick }) => {
  const Icon = dir === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`featured-projects-${dir}`}
      aria-label={dir === 'prev' ? 'Forrige projekter' : 'Næste projekter'}
      className="p-2 text-gray-500 hover:text-gray-900 transition-colors duration-300"
    >
      <Icon className="w-6 h-6" strokeWidth={1.25} />
    </button>
  );
};

const ProjectCard = ({ p }) => {
  const r = p.w / p.h;
  return (
    <Link
      to={`/projekter/${p.slug}`}
      data-testid={`featured-project-${p.slug}`}
      className="group snap-start shrink-0 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-900 focus-visible:ring-offset-4"
    >
      <div
        className="w-[min(85vw,var(--wm))] md:w-[var(--wd)] overflow-hidden bg-neutral-200"
        style={{ aspectRatio: `${p.w} / ${p.h}`, '--wm': `${Math.round(360 * r)}px`, '--wd': `${Math.round(560 * r)}px` }}
      >
        <img
          src={img(p.slug, 1200)}
          srcSet={`${img(p.slug, 600)} 600w, ${img(p.slug, 1200)} 1200w`}
          sizes={`(max-width: 767px) ${Math.round(360 * r)}px, ${Math.round(560 * r)}px`}
          width={p.w}
          height={p.h}
          alt={p.title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:opacity-90 transition-opacity duration-500"
        />
      </div>
      <p className="mt-4 text-gray-900 text-lg font-light">{p.title}</p>
    </Link>
  );
};

export const FeaturedProjects = () => {
  const track = useRef(null);
  const scroll = (dir) => {
    const el = track.current;
    el.scrollBy({ left: (dir === 'next' ? 1 : -1) * el.clientWidth * 0.7, behavior: 'smooth' });
  };

  return (
    <section data-testid="featured-projects-section" className="py-32 bg-neutral-50">
      <div className="max-w-6xl mx-auto px-8 md:px-16 xl:px-0 flex items-end justify-between mb-12">
        <h2 className="text-3xl md:text-4xl font-light text-gray-900">Udvalgte projekter</h2>
        <div className="hidden md:flex items-center space-x-2">
          <ArrowButton dir="prev" onClick={() => scroll('prev')} />
          <ArrowButton dir="next" onClick={() => scroll('next')} />
        </div>
      </div>
      <div
        ref={track}
        data-testid="featured-projects-track"
        tabIndex={0}
        aria-label="Udvalgte projekter"
        className="flex gap-8 md:gap-12 overflow-x-auto snap-x snap-mandatory scroll-pl-8 md:scroll-pl-[max(4rem,calc((100vw-72rem)/2))] pl-8 md:pl-[max(4rem,calc((100vw-72rem)/2))] pr-8 md:pr-16 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden focus:outline-none"
      >
        {featured.map((p) => <ProjectCard key={p.slug} p={p} />)}
      </div>
      <div className="max-w-6xl mx-auto px-8 md:px-16 xl:px-0 mt-6 md:mt-12">
        <Link
          to="/projekter"
          data-testid="featured-projects-see-all"
          className="inline-flex items-center space-x-3 text-gray-900 text-lg font-light border-b border-gray-900 pb-2 hover:text-gray-600 hover:border-gray-600 transition-colors duration-300 group"
        >
          <span>Se alle projekter</span>
          <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
        </Link>
      </div>
    </section>
  );
};
